import React, { useEffect, useMemo, useState } from "react";
import { Button, Container, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import NavBar from "../components/nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/description.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBed,
  FaCalendarAlt,
  FaCheckCircle,
  FaChild,
  FaClock,
  FaEnvelope,
  FaMapMarkerAlt,
  FaMoon,
  FaPhone,
  FaStar,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { notifyError, notifySuccess } from "../utils/notify";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(value);

const getNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const difference = checkOut.getTime() - checkIn.getTime();
  return Math.max(0, Math.ceil(difference / (1000 * 60 * 60 * 24)));
};

const Description = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location.state || {};

  const gallery = useMemo(() => {
    const images = Array.isArray(details.gallery)
      ? details.gallery.filter(Boolean)
      : [];
    return [...new Set([details.image, ...images].filter(Boolean))];
  }, [details.gallery, details.image]);

  const [selectedImage, setSelectedImage] = useState("");
  const [imageScores, setImageScores] = useState({});
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pricePerNight = Number(details.pricePerNight || details.price) || 0;
  const rating = Math.min(5, Math.max(0, Number(details.rating) || 0));
  const nights = getNights(checkInDate, checkOutDate);
  const total = nights * pricePerNight;
  const imageCountLabel = gallery.length === 1 ? "1 photo" : `${gallery.length} photos`;

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
    setImageScores({});
  }, [gallery]);

  useEffect(() => {
    if (!gallery.length) return;

    const bestImage = gallery.reduce((best, src) => {
      const bestScore = imageScores[best] || 0;
      const score = imageScores[src] || 0;
      return score > bestScore ? src : best;
    }, selectedImage || gallery[0]);

    if (bestImage && bestImage !== selectedImage) {
      setSelectedImage(bestImage);
    }
  }, [gallery, imageScores, selectedImage]);

  const handleImageLoad = (src, event) => {
    const { naturalHeight, naturalWidth } = event.currentTarget;
    const score = naturalHeight * naturalWidth;
    setImageScores((current) =>
      current[src] === score ? current : { ...current, [src]: score }
    );
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setCheckInDate(start);
    setCheckOutDate(end);
    setBookingStatus("");
  };

  const formatDate = (date) => date.toISOString().split("T")[0];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBookingStatus("");

    if (!checkInDate || !checkOutDate) {
      setBookingStatus("Please select check-in and check-out dates.");
      notifyError("Please select check-in and check-out dates before reserving.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://kim-tour-app-backend.onrender.com/api/accommodation/book",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            email,
            phone,
            checkIn: formatDate(checkInDate),
            checkOut: formatDate(checkOutDate),
            adults,
            children,
            comments: specialRequests,
            accommodation: details.title,
            businessEmail: details.email || "edward.matiwane@gmail.com",
            estimatedTotal: total,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setBookingStatus(
          `Booking request sent. Reference: ${data.bookingId || "pending"}`
        );
        notifySuccess(
          `Booking request sent successfully. Reference: ${
            data.bookingId || "pending"
          }`
        );
        setCheckInDate(null);
        setCheckOutDate(null);
        setAdults(1);
        setChildren(0);
        setName("");
        setEmail("");
        setPhone("");
        setSpecialRequests("");
      } else {
        const message = data.message || "Booking failed. Please try again.";
        setBookingStatus(message);
        notifyError(message);
      }
    } catch (error) {
      const message = "We could not send the booking request. Please try again.";
      setBookingStatus(message);
      notifyError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!details.title) {
    return (
      <div className="description-page">
        <NavBar />
        <Container className="hotel-detail-container">
          <Button variant="light" onClick={handleBack} className="hotel-back-button">
            <FaArrowLeft />
            Back
          </Button>
          <div className="hotel-empty-state">
            <h2>Accommodation details unavailable</h2>
            <p>Please return to the accommodation list and choose a place to stay.</p>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div className="description-page">
      <NavBar />
      <main className="hotel-detail-container">
        <div className="hotel-title-row">
          <button className="hotel-back-button" onClick={handleBack} type="button">
            <FaArrowLeft />
          </button>
          <div>
            <p className="hotel-eyebrow">{details.category || "Accommodation"}</p>
            <h1>{details.title}</h1>
            <div className="hotel-meta">
              <span>
                <FaMapMarkerAlt />
                {details.location || "Kimberley"}
              </span>
              <span>
                <FaStar />
                {rating}/5 guest rating
              </span>
            </div>
          </div>
        </div>

        <section className="hotel-gallery" aria-label={`${details.title} gallery`}>
          <div className="hotel-main-photo">
            {selectedImage ? (
              <img src={selectedImage} alt={details.title} />
            ) : (
              <div className="hotel-photo-placeholder">
                <span>{details.category || "Kimberley stay"}</span>
              </div>
            )}
            <span className="hotel-photo-count">{imageCountLabel}</span>
          </div>

          <div className="hotel-thumbnails">
            {gallery.slice(0, 6).map((src, index) => (
              <button
                className={`hotel-thumbnail ${
                  selectedImage === src ? "active" : ""
                }`}
                key={src}
                onClick={() => setSelectedImage(src)}
                type="button"
              >
                <img
                  src={src}
                  alt={`${details.title} view ${index + 1}`}
                  onLoad={(event) => handleImageLoad(src, event)}
                />
              </button>
            ))}
          </div>
        </section>

        <div className="hotel-content-grid">
          <section className="hotel-main-content">
            <div className="hotel-panel hotel-review-panel">
              <div className="hotel-icon-badge primary">
                <FaStar />
              </div>
              <div>
                <p className="hotel-panel-label">Guest Reviews</p>
                <div className="hotel-stars">
                  <strong>{rating}</strong>
                  {Array.from({ length: 5 }, (_, index) => (
                    <FaStar
                      key={index}
                      className={index < rating ? "filled" : ""}
                    />
                  ))}
                  <span>({details.reviewsCount || "100+"} reviews)</span>
                </div>
              </div>
            </div>

            <div className="hotel-section">
              <h2>About This Stay</h2>
              <p>{details.description || "A comfortable Kimberley accommodation option."}</p>
            </div>

            <div className="hotel-section">
              <h2>Contact Information</h2>
              <div className="hotel-info-list">
                <div className="hotel-info-item">
                  <span className="hotel-icon-badge cyan">
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Location</strong>
                    <p>{details.location || "Location not supplied"}</p>
                  </div>
                </div>
                <div className="hotel-info-item">
                  <span className="hotel-icon-badge green">
                    <FaPhone />
                  </span>
                  <div>
                    <strong>Phone Number</strong>
                    <p>{details.phone || "Contact number not supplied"}</p>
                  </div>
                </div>
                <div className="hotel-info-item">
                  <span className="hotel-icon-badge amber">
                    <FaEnvelope />
                  </span>
                  <div>
                    <strong>Email Address</strong>
                    <p>{details.email || "Email not supplied"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="hotel-section">
              <h2>Stay Highlights</h2>
              <div className="hotel-highlights">
                <div>
                  <FaBed />
                  <strong>Comfort Rooms</strong>
                  <span>Private rooms with bedding and guest essentials.</span>
                </div>
                <div>
                  <FaClock />
                  <strong>14:00 / 10:00</strong>
                  <span>Standard check-in and check-out times.</span>
                </div>
                <div>
                  <FaCheckCircle />
                  <strong>Guest Ready</strong>
                  <span>Good for short visits, business trips, and weekends.</span>
                </div>
              </div>
            </div>
          </section>

          <aside className="hotel-sidebar">
            <div className="hotel-booking-card">
              <div className="hotel-rate-line">
                <div>
                  <span>From</span>
                  <strong>
                    {pricePerNight > 0 ? formatCurrency(pricePerNight) : "Contact"}
                  </strong>
                  <small>{pricePerNight > 0 ? "per night" : "for rates"}</small>
                </div>
                <FaMoon />
              </div>

              {details.mapLink && (
                <a
                  href={details.mapLink}
                  className="hotel-map-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaMapMarkerAlt />
                  Get Directions
                </a>
              )}

              <Form onSubmit={handleSubmit} className="hotel-booking-form">
                <Form.Group>
                  <Form.Label>
                    <FaCalendarAlt />
                    Dates
                  </Form.Label>
                  <DatePicker
                    selectsRange
                    startDate={checkInDate}
                    endDate={checkOutDate}
                    onChange={handleDateChange}
                    minDate={new Date()}
                    placeholderText="Select check-in and check-out"
                    className="form-control hotel-date-input"
                    required
                  />
                </Form.Group>

                <div className="hotel-guest-group">
                  <p>
                    <FaUsers />
                    Guests
                  </p>
                  <div className="hotel-stepper-row">
                    <span>
                      <FaUser />
                      Adults
                    </span>
                    <div className="hotel-stepper">
                      <button
                        type="button"
                        onClick={() => setAdults((value) => Math.max(1, value - 1))}
                        disabled={adults <= 1}
                      >
                        -
                      </button>
                      <strong>{adults}</strong>
                      <button
                        type="button"
                        onClick={() => setAdults((value) => value + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="hotel-stepper-row">
                    <span>
                      <FaChild />
                      Children
                    </span>
                    <div className="hotel-stepper">
                      <button
                        type="button"
                        onClick={() =>
                          setChildren((value) => Math.max(0, value - 1))
                        }
                        disabled={children <= 0}
                      >
                        -
                      </button>
                      <strong>{children}</strong>
                      <button
                        type="button"
                        onClick={() => setChildren((value) => value + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <Form.Group>
                  <Form.Label>Your Information</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                  />
                  <Form.Control
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                  <Form.Control
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Special Requests</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Arrival time, accessibility needs, dietary notes..."
                    value={specialRequests}
                    onChange={(event) => setSpecialRequests(event.target.value)}
                  />
                </Form.Group>

                <div className="hotel-price-summary">
                  <div>
                    <span>{nights || 0} nights</span>
                    <strong>
                      {total > 0 ? formatCurrency(total) : "Total shown after dates"}
                    </strong>
                  </div>
                  <small>Estimated total. Final availability is confirmed by the host.</small>
                </div>

                {bookingStatus && (
                  <p className="hotel-booking-status">{bookingStatus}</p>
                )}

                <Button
                  variant="primary"
                  type="submit"
                  className="hotel-reserve-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending Request..." : "Reserve Now"}
                </Button>
              </Form>
            </div>
          </aside>
        </div>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={details.title} category="Accommodation" />
      </section>
      <Footer />
    </div>
  );
};

export default Description;
